<script setup>
import { ref, watch, computed } from 'vue';
import TextInput from './text-input.vue';
import SelectInput from './select-input.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Enter value'
  },
  addButtonText: {
    type: String,
    default: 'Add Field'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  minItems: {
    type: Number,
    default: 0
  },
  maxItems: {
    type: Number,
    default: null
  },
  selectOptions: {
    type: Array,
    default: null
  },
  selectPlaceholder: {
    type: String,
    default: 'Select type'
  },
  selectWidth: {
    type: String,
    default: 'w-40'
  }
});

const emit = defineEmits(['update:modelValue']);

const MAX_FIELDS = 10;

const limitFields = (fieldsArray) => {
  const maxAllowed = props.maxItems !== null ? Math.min(props.maxItems, MAX_FIELDS) : MAX_FIELDS;
  return fieldsArray.slice(0, maxAllowed);
};

const normalizeField = (field) => {
  if (props.selectOptions) {
    if (typeof field === 'string') {
      return { type: props.selectOptions[0]?.value || '', value: field };
    }
    return field || { type: props.selectOptions[0]?.value || '', value: '' };
  }
  return field || '';
};

const normalizeFields = (fieldsArray) => {
  if (!fieldsArray || fieldsArray.length === 0) {
    return props.selectOptions ? [{ type: props.selectOptions[0]?.value || '', value: '' }] : [''];
  }
  return fieldsArray.map(normalizeField);
};

const initialFields = limitFields(normalizeFields(props.modelValue));
if (initialFields.length === 0 && props.selectOptions) {
  initialFields.push({ type: props.selectOptions[0]?.value || '', value: '' });
} else if (initialFields.length === 0 && props.minItems > 0) {
  initialFields.push('');
}

const fields = ref(initialFields);

watch(() => props.modelValue, (newValue) => {
  const normalized = limitFields(normalizeFields(newValue));
  if (normalized.length === 0 && props.selectOptions) {
    normalized.push({ type: props.selectOptions[0]?.value || '', value: '' });
  } else if (normalized.length === 0 && props.minItems > 0) {
    normalized.push('');
  }
  
  if (JSON.stringify(normalized) !== JSON.stringify(fields.value)) {
    fields.value = normalized;
  }
}, { deep: true });

const addField = () => {
  const maxAllowed = props.maxItems !== null ? Math.min(props.maxItems, MAX_FIELDS) : MAX_FIELDS;
  if (fields.value.length < maxAllowed) {
    const newField = props.selectOptions 
      ? { type: props.selectOptions[0]?.value || '', value: '' }
      : '';
    fields.value.push(newField);
    updateModelValue();
  }
};

const removeField = (index) => {
  if (fields.value.length > props.minItems) {
    fields.value.splice(index, 1);
    updateModelValue();
  }
};

const updateField = (index, value) => {
  if (props.selectOptions) {
    if (typeof fields.value[index] === 'object' && fields.value[index] !== null) {
      fields.value[index] = { ...fields.value[index], value };
    } else {
      fields.value[index] = { type: props.selectOptions[0]?.value || '', value };
    }
  } else {
    fields.value[index] = value;
  }
  updateModelValue();
};

const updateFieldType = (index, type) => {
  if (props.selectOptions) {
    if (typeof fields.value[index] === 'object' && fields.value[index] !== null) {
      fields.value[index] = { ...fields.value[index], type };
    } else {
      fields.value[index] = { type, value: fields.value[index] || '' };
    }
    updateModelValue();
  }
};

const updateModelValue = () => {
  emit('update:modelValue', [...fields.value]);
};

const canAddMore = computed(() => {
  const maxAllowed = props.maxItems !== null ? Math.min(props.maxItems, MAX_FIELDS) : MAX_FIELDS;
  return fields.value.length < maxAllowed;
});

const canRemove = computed(() => {
  return (index) => fields.value.length > props.minItems && fields.value.length > 1;
});
</script>

<template>
  <div class="multi-text-input">
    <div class="multi-text-input__fields">
      <div 
        v-for="(field, index) in fields" 
        :key="index"
        class="multi-text-input__field-wrapper"
      >
        <div class="multi-text-input__input-container">
          <SelectInput
            v-if="selectOptions"
            :id="`multi-select-${index}`"
            :model-value="typeof field === 'object' ? field.type : ''"
            @update:model-value="updateFieldType(index, $event)"
            :options="selectOptions"
            :placeholder="selectPlaceholder"
            :disabled="disabled"
            :class="['multi-text-input__select', selectWidth]"
          />
          <TextInput
            :id="`multi-input-${index}`"
            :model-value="typeof field === 'object' ? field.value : field"
            @update:model-value="updateField(index, $event)"
            :placeholder="placeholder"
            :disabled="disabled"
            :required="required && index === 0"
            :class="['multi-text-input__input', { 'multi-text-input__input--with-btn': canRemove(index) }]"
          />
          <button
            v-if="canRemove(index)"
            @click="removeField(index)"
            type="button"
            class="multi-text-input__remove-btn"
            :disabled="disabled"
            :aria-label="`Remove field ${index + 1}`"
          >
            <i class="uil uil-times"></i>
          </button>
        </div>
      </div>
    </div>
    
    <button
      v-if="canAddMore"
      @click="addField"
      type="button"
      class="multi-text-input__add-btn"
      :disabled="disabled"
    >
      <i class="uil uil-plus mr-2"></i>
      {{ addButtonText }}
    </button>
  </div>
</template>

<style scoped lang="postcss">
.multi-text-input {
  @apply w-full;
}

.multi-text-input__fields {
  @apply flex flex-col space-y-3;
}

.multi-text-input__field-wrapper {
  @apply w-full min-w-0;
}

.multi-text-input__input-container {
  @apply grid grid-cols-3 gap-3 items-center w-full min-w-0 relative;
}

.multi-text-input__select {
  @apply col-span-1;
}

.multi-text-input__input {
  @apply col-span-2;
}

.multi-text-input__input--with-btn {
  @apply pr-12;
}

.multi-text-input__remove-btn {
  @apply absolute right-0 flex items-center justify-center w-10 h-10 rounded-[10px]
    bg-red-600/50 border border-red-600 text-white hover:bg-red-700 transition-colors
    disabled:opacity-50 disabled:cursor-not-allowed;
}

.multi-text-input__remove-btn i {
  @apply text-lg;
}

.multi-text-input__add-btn {
  @apply mt-3 flex text-sm items-center justify-center px-4 py-2.5
  rounded-[10px]
  bg-echoes-primary/50 border border-echoes-primary hover:bg-echoes-primary text-white transition-colors
  disabled:opacity-50 disabled:cursor-not-allowed;
}
</style>
